"use client";

import React, { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Search as SearchIcon, Sparkles, BookOpenCheck, ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
const sampleQueries = [{
  text: "Why Is My Dog Licking His Paws?",
  category: "Dog Health"
}, {
  text: "Why Is My Cat Not Eating?",
  category: "Cat Health"
}, {
  text: "Puppy Feeding Schedule",
  category: "Nutrition"
}, {
  text: "Night Meowing in Cats",
  category: "Behavior"
}];
function SearchFormInner({
  onSelectQuery
}: {
  onSelectQuery?: (q: string) => void;
}) {
  const {
    isSubmitting,
    handleSubmit,
    control
  } = useWvcForm();
  return <Form onSubmit={handleSubmit} className="w-full space-y-0">
      <div className="relative flex flex-col sm:flex-row items-stretch gap-2.5 p-2 bg-background rounded-xl border border-border shadow-md focus-within:border-ring focus-within:ring-2 focus-within:ring-ring/20 transition-all">
        <div className="relative flex-1 flex items-center">
          <SearchIcon className="absolute left-3.5 size-5 text-muted-foreground pointer-events-none" />
          <FormField control={control} name="searchQuery" render={({
          field
        }) => <FormItem className="w-full gap-0">
                <FormControl>
                  <Input {...field} type="search" placeholder="Can dogs eat blueberries?" className="h-12 pl-11 pr-4 border-0 shadow-none text-base md:text-lg placeholder:text-muted-foreground/75 bg-transparent focus-visible:ring-0 focus-visible:border-0" />
                </FormControl>
                <FormMessage />
              </FormItem>} />
        </div>

        <Button type="submit" disabled={isSubmitting} className="h-12 px-7 rounded-lg font-semibold bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm transition-colors flex items-center justify-center gap-2 text-base">
          <span>Search</span>
          <ArrowRight className="size-4" />
        </Button>
      </div>
    </Form>;
}
export default function PetSearch() {
  const prefersReduced = useReducedMotion();
  const [activeQuery, setActiveQuery] = useState("");
  const handleQueryClick = (queryText: string) => {
    setActiveQuery(queryText);
  };
  return <section id="search" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border transition-colors">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
        <motion.div initial={prefersReduced ? {
        opacity: 1
      } : {
        opacity: 0,
        y: 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.2
      }} transition={{
        duration: 0.45,
        ease: "easeOut"
      }} className="bg-card text-card-foreground rounded-2xl border border-border p-6 sm:p-10 md:p-14 shadow-sm relative overflow-hidden">
          {/* Subtle decorative medical evidence indicator */}
          <div className="absolute top-0 left-0 right-0 h-1 bg-border" />
          <div className="absolute top-0 left-10 w-24 h-1 bg-chart-1" />

          <div className="text-center max-w-2xl mx-auto space-y-4 mb-8">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-muted border border-border text-xs font-semibold tracking-wider text-muted-foreground uppercase">
              <BookOpenCheck className="size-3.5 text-chart-1" />
              <span>Evidence-informed pet guidance</span>
            </div>

            <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight leading-tight text-card-foreground">
              What would you like to know about your pet?
            </h2>

            <p className="text-muted-foreground text-base sm:text-lg leading-relaxed">
              Search clinical articles, breed health guides, and veterinary-reviewed nutrition advice.
            </p>
          </div>

          <div className="max-w-2xl mx-auto space-y-6">
            <FormProvider formId="pet-knowledge-search" sectionName="search-discovery" defaultValues={{
            searchQuery: activeQuery
          }} validationRules={{
            searchQuery: {
              required: true,
              minLength: 2
            }
          }} submitText="Search">
              <SearchFormInner onSelectQuery={handleQueryClick} />
            </FormProvider>

            {/* Quick-tap question pills */}
            <div className="space-y-3 pt-2">
              <div className="flex items-center justify-between text-xs font-medium text-muted-foreground px-1">
                <span className="flex items-center gap-1.5">
                  <Sparkles className="size-3.5 text-chart-1" />
                  Popular veterinary questions
                </span>
                <Link to="/SearchResultsPage" className="text-xs font-semibold text-foreground hover:underline">
                  Browse all topics
                </Link>
              </div>

              <div className="flex flex-wrap gap-2 sm:gap-2.5">
                {sampleQueries.map((item, i) => <Link key={item.text} to="/SearchResultsPage" data-index={i} onClick={() => handleQueryClick(item.text)} className="inline-flex items-center gap-2 px-3.5 py-2 rounded-full text-xs sm:text-sm font-medium bg-background text-foreground border border-border hover:border-chart-1 hover:bg-muted/50 transition-[color,background-color,border-color,box-shadow] shadow-2xs group">
                    <span>{item.text}</span>
                    <span className="text-[10px] uppercase font-semibold text-muted-foreground/80 px-1.5 py-0.5 rounded bg-muted group-hover:bg-background transition-colors">
                      {item.category}
                    </span>
                  </Link>)}
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}